import React, { useState } from 'react';

import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';

import { ChargingPolicy } from 'subdomains/cpo/types/charging-policy';
import {
  Button,
  Container,
  Form,
  Header,
  Message,
  Divider,
  FormGroup,
} from 'semantic';
import { chargingPolicySchema } from '../../validation';
import { FeatureFlag } from 'components';
import { FeatureFlags } from 'contexts/features';
import InputField from 'components/form-fields/formik/InputField';
import DropdownField from 'components/form-fields/formik/DropdownField';
import { useUser } from 'contexts/user';
import { TariffRules } from './TariffRules';
import PriceField from 'components/form-fields/formik/PriceField';
import ChargingPolicyAssociationSelection from 'subdomains/cpo/screens/policies-and-groups/screens/provider-admin/charging-policies/form/ChargingPolicyAssociationSelection';
import { ApiError, request } from 'utils/api';
import { useHistory, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { SegmentLoader } from 'subdomains/cpo/components/SegmentLoader';
import { FormikErrorsMessage } from 'subdomains/cpo/components/FormikErrorsMessage';
import { CHARGING_STATIONS_POLICIES_AND_GROUPS_CHARGING_POLICIES_ROUTE } from 'subdomains/cpo/router/routes';
import { CHARGING_POLICIES_V1_API_URL } from 'subdomains/cpo/router/api';
import { errorCodeTranslation } from 'subdomains/cpo/utils/translations';

type Props = {
  mode: 'create' | 'edit';
};

const defaultInitialValues: ChargingPolicy = {
  id: '',
  label: '',
  description: '',
  currency: 'EUR',
  sessionThresholds: {
    minTimeMinutes: undefined,
    minKwhConsumed: undefined,
    maxCost: undefined,
  },
  tariffRules: [],
  associatedChargingGroups: [],
};

export const ChargingPolicyForm = ({ mode = 'create' }: Props) => {
  const { t } = useTranslation();
  const [error, setError] = React.useState<ApiError | null>(null);
  const historyStack = useHistory();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [isEditingTariffRules, setIsEditingTariffRules] = useState(false);

  const { data: fetchedData, isPending } = useQuery({
    queryKey: [CHARGING_POLICIES_V1_API_URL, id],
    queryFn: () =>
      request({
        method: 'GET',
        path: `${CHARGING_POLICIES_V1_API_URL}/${id}`,
      }),
    enabled: mode === 'edit' && !!id,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const { provider } = useUser();

  const mutation = useMutation({
    mutationFn: (values: ChargingPolicy) => {
      if (mode === 'edit') {
        return request({
          method: 'PUT',
          path: `${CHARGING_POLICIES_V1_API_URL}/${values?.id}`,
          body: values,
        });
      }
      return request({
        method: 'POST',
        path: CHARGING_POLICIES_V1_API_URL,
        body: values,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [CHARGING_POLICIES_V1_API_URL],
      });
      if (mode === 'create') {
        historyStack.push(
          CHARGING_STATIONS_POLICIES_AND_GROUPS_CHARGING_POLICIES_ROUTE
        );
      }
    },
    onError: (e: ApiError) => {
      setError(e);
    },
  });

  const submit = async (values: ChargingPolicy) => {
    setError(null);
    mutation.mutate(values);
  };

  const cancel = () => {
    historyStack.push(
      CHARGING_STATIONS_POLICIES_AND_GROUPS_CHARGING_POLICIES_ROUTE
    );
  };

  if (isPending && mode === 'edit') {
    return <SegmentLoader height={200} />;
  }

  const normalizeInitialValues = (values: ChargingPolicy) => {
    if (!values?.tariffRules?.length) {
      return values;
    }

    for (const rule of values.tariffRules) {
      // if start is set, convert it to a Date object
      if (rule.validityPeriod) {
        rule.validityPeriod.start &&= new Date(rule.validityPeriod.start);
        rule.validityPeriod.end &&= new Date(rule.validityPeriod.end);
      }

      for (const component of rule.tariff.components) {
        if (component.restrictions?.date) {
          component.restrictions.date.start &&= new Date(
            component.restrictions.date.start
          );
          component.restrictions.date.end &&= new Date(
            component.restrictions.date.end
          );
        }
      }
    }

    return values;
  };

  const initialValues = normalizeInitialValues(
    fetchedData?.data || defaultInitialValues
  );

  return (
    <FeatureFlag feature={FeatureFlags.PoliciesAndGroups}>
      <Container>
        <Formik
          initialValues={initialValues}
          enableReinitialize
          validationSchema={chargingPolicySchema}
          onSubmit={submit}
          onReset={cancel}>
          {({
            values,
            status,
            handleSubmit,
            handleReset,
            setFieldValue,
            errors,
            submitCount,
          }) => (
            <Form>
              <Header
                as="h2"
                content={
                  mode === 'create'
                    ? t(
                        'providerAdmin.chargingPolicyForm.createTitle',
                        'Create Charging Policy'
                      )
                    : t(
                        'providerAdmin.chargingPolicyForm.editTitle',
                        'Edit Charging Policy'
                      )
                }
                style={{ marginBottom: 30 }}
              />
              {status && <Message error content={status} />}
              <Header
                as="h3"
                content={t(
                  'providerAdmin.chargingPolicyForm.generalInfoTitle',
                  'General Info'
                )}
              />
              <InputField
                name="label"
                label={t('providerAdmin.chargingPolicyForm.labelLabel', 'Name')}
                required
              />
              <InputField
                name="description"
                label={t(
                  'providerAdmin.chargingPolicyForm.descriptionLabel',
                  'Description'
                )}
              />
              <DropdownField
                name="currency"
                selection
                required
                label={t(
                  'providerAdmin.chargingPolicyForm.currencyLabel',
                  'Currency'
                )}
                options={
                  provider?.billingCurrencies.map((currency) => ({
                    key: currency,
                    value: currency,
                    text: currency,
                  })) || []
                }
              />
              <Divider hidden />
              <Header
                as="h3"
                content={t(
                  'providerAdmin.chargingPolicyForm.pricingTitle',
                  'Pricing'
                )}
              />
              <Message
                info
                content={t(
                  'providerAdmin.chargingPolicyForm.pricingDescriptionMessageText',
                  'Tariffs are evaluated based on descending order (from top to bottom). Make sure that more specific tariffs (e.g. with specific payment options or customer groups) are first in the list and general tariffs (e.g. all payment options, applies to everyone) last in the list.'
                )}
              />
              <TariffRules
                values={values?.tariffRules}
                onStartEditing={() => setIsEditingTariffRules(true)}
                onEditFinished={() => setIsEditingTariffRules(false)}
              />

              <Divider hidden />
              <Header
                as="h3"
                content={t(
                  'providerAdmin.chargingPolicyForm.sessionLimitsTitle',
                  'Session limits'
                )}
              />
              <FormGroup style={{ marginLeft: '0' }}>
                <PriceField
                  name="sessionThresholds.maxCost"
                  currency={values?.currency}
                  label={t(
                    'providerAdmin.chargingPolicyForm.maxCostLabel',
                    'Max session cost'
                  )}
                  wrapperStyle={{ width: 200 }}
                />
              </FormGroup>
              <Divider hidden />
              <Header
                as="h3"
                content={t(
                  'providerAdmin.chargingPolicyForm.policyAssociationTitle',
                  'Policy association'
                )}
              />
              <ChargingPolicyAssociationSelection
                values={values?.associatedChargingGroups}
                onChange={(values) =>
                  setFieldValue('associatedChargingGroups', values)
                }
              />
              <div style={{ marginTop: 50 }}>
                {error && (
                  <Message error>
                    {errorCodeTranslation(t, error.type, error.message)}
                  </Message>
                )}
                {submitCount > 0 && <FormikErrorsMessage errors={errors} />}
                {isEditingTariffRules && (
                  <Message warning>
                    {mode === 'create'
                      ? t(
                          'providerAdmin.chargingPolicyForm.policyAssociationDescriptionCreate',
                          'Save or cancel the tariff forms before proceeding with policy creation'
                        )
                      : t(
                          'providerAdmin.chargingPolicyForm.policyAssociationDescriptionUpdate',
                          'Save or cancel the tariff forms before proceeding with policy update'
                        )}
                  </Message>
                )}
              </div>
              <div
                style={{
                  display: 'flex',
                  gap: 10,
                  justifyContent: 'flex-start',
                  marginTop: 20,
                }}>
                <Button
                  disabled={isEditingTariffRules}
                  content={
                    mode === 'create'
                      ? t(
                          'providerAdmin.chargingPolicyForm.createPolicyButtonText',
                          'Create Policy'
                        )
                      : t(
                          'providerAdmin.chargingPolicyForm.updatePolicyButtonText',
                          'Update Policy'
                        )
                  }
                  as="button"
                  style={{ marginLeft: '0' }}
                  onClick={handleSubmit}
                  loading={mutation.isPending}
                />
                <Button
                  content={t(
                    'providerAdmin.chargingPolicyForm.cancelButtonText',
                    'Discard'
                  )}
                  as="button"
                  basic
                  style={{ marginLeft: '0' }}
                  onClick={handleReset}
                />
              </div>
            </Form>
          )}
        </Formik>
      </Container>
    </FeatureFlag>
  );
};
